import { lazy, Suspense, forwardRef } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AIAssistantPanel } from "@/components/AIAssistantPanel";

// Lazy load all pages for code splitting
const Index = lazy(() => import("./pages/Index"));
const Features = lazy(() => import("./pages/Features"));
const Pricing = lazy(() => import("./pages/Pricing"));
const About = lazy(() => import("./pages/About"));
const Industries = lazy(() => import("./pages/Industries"));
const Contact = lazy(() => import("./pages/Contact"));
const Security = lazy(() => import("./pages/Security"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));

const Dashboard = lazy(() => import("./pages/Dashboard"));
const Sheets = lazy(() => import("./pages/Sheets"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Team = lazy(() => import("./pages/Team"));
const Admin = lazy(() => import("./pages/Admin"));
const Settings = lazy(() => import("./pages/Settings"));
const DailyReport = lazy(() => import("./pages/DailyReport"));
const FinanceDashboard = lazy(() => import("./pages/finance/FinanceDashboard"));
const ChartOfAccounts = lazy(() => import("./pages/finance/ChartOfAccounts"));
const JournalEntries = lazy(() => import("./pages/finance/JournalEntries"));
const Vendors = lazy(() => import("./pages/finance/Vendors"));
const Budgets = lazy(() => import("./pages/finance/Budgets"));
const Tasks = lazy(() => import("./pages/Tasks"));
const TimeTracking = lazy(() => import("./pages/TimeTracking"));
const Attendance = lazy(() => import("./pages/Attendance"));
const Payroll = lazy(() => import("./pages/Payroll"));
const Automations = lazy(() => import("./pages/Automations"));
const CRMDashboard = lazy(() => import("./pages/CRMDashboard"));
const Projects = lazy(() => import("./pages/Projects"));
const Clients = lazy(() => import("./pages/Clients"));
const Expenses = lazy(() => import("./pages/Expenses"));
const FollowUps = lazy(() => import("./pages/FollowUps"));
const ApprovalCenter = lazy(() => import("./components/tools/ApprovalCenter"));
const CommandCenter = lazy(() => import("./components/dashboard/CommandCenter"));
const DocumentManager = lazy(() => import("./components/tools/DocumentManager"));
const ComplianceCenter = lazy(() => import("./components/tools/ComplianceCenter"));
const AgileBoard = lazy(() => import("./components/projects/AgileBoard"));
const CommunicationAutomation = lazy(() => import("./components/crm/CommunicationAutomation"));
const InventoryIntelligence = lazy(() => import("./components/inventory/InventoryIntelligence"));
const HRPerformanceSuite = lazy(() => import("./components/hr/HRPerformanceSuite"));
const Communications = lazy(() => import("./pages/Communications"));
const Performance = lazy(() => import("./pages/Performance"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const OrganizationSettings = lazy(() => import("./pages/OrganizationSettings"));
const CallerWorkstation = lazy(() => import("./pages/CallerWorkstation"));
const ExcelDataMigrationPage = lazy(() => import("./pages/ExcelDataMigrationPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

// HR Pages
const EmployeeProfile = lazy(() => import("./pages/hr/EmployeeProfile"));
const AddEmployeeWizard = lazy(() => import("./pages/hr/AddEmployeeWizard"));
const LeaveTypes = lazy(() => import("./pages/hr/LeaveTypes"));
const LeaveApprovals = lazy(() => import("./pages/hr/LeaveApprovals"));
const SalaryComponents = lazy(() => import("./pages/hr/SalaryComponents"));
const LeaveCalendar = lazy(() => import("./pages/hr/LeaveCalendar"));
const LeaveBalance = lazy(() => import("./pages/hr/LeaveBalance"));
const SalaryStructures = lazy(() => import("./pages/hr/SalaryStructures"));
const PayrollProcessing = lazy(() => import("./pages/hr/PayrollProcessing"));
const LoanManagement = lazy(() => import("./pages/hr/LoanManagement"));
const JobOpenings = lazy(() => import("./pages/hr/JobOpenings"));
const JobApplications = lazy(() => import("./pages/hr/JobApplications"));
const InterviewManagement = lazy(() => import("./pages/hr/InterviewManagement"));
const DepartmentManagement = lazy(() => import("./pages/hr/DepartmentManagement"));
const OrganizationChart = lazy(() => import("./pages/hr/OrganizationChart"));
const LeaveEncashment = lazy(() => import("./pages/hr/LeaveEncashment"));
const BulkEmployeeImport = lazy(() => import("./pages/hr/BulkEmployeeImport"));
const AttendanceCorrections = lazy(() => import("./pages/hr/AttendanceCorrections"));
const AttendancePolicies = lazy(() => import("./pages/hr/AttendancePolicies"));
const OnboardingChecklist = lazy(() => import("./pages/hr/OnboardingChecklist"));
const OfferLetterGenerator = lazy(() => import("./pages/hr/OfferLetterGenerator"));
const EmployeeSalaryView = lazy(() => import("./pages/hr/EmployeeSalaryView"));
const PayrollHistory = lazy(() => import("./pages/hr/PayrollHistory"));
const EmployeeStatusManagement = lazy(() => import("./pages/hr/EmployeeStatusManagement"));
const TerminationProcess = lazy(() => import("./pages/hr/TerminationProcess"));
const BenefitsAdministration = lazy(() => import("./pages/hr/BenefitsAdministration"));
const TrainingManagement = lazy(() => import("./pages/hr/TrainingManagement"));
const SkillsMatrix = lazy(() => import("./pages/hr/SkillsMatrix"));

// Finance Pages
const CreditDebitNotes = lazy(() => import("./pages/finance/CreditDebitNotes"));
const PaymentRecording = lazy(() => import("./pages/finance/PaymentRecording"));
const BillApprovalQueue = lazy(() => import("./pages/finance/BillApprovalQueue"));
const AccountLedger = lazy(() => import("./pages/finance/AccountLedger"));
const CustomerInvoices = lazy(() => import("./pages/finance/CustomerInvoices"));
const VendorBills = lazy(() => import("./pages/finance/VendorBills"));
const ARDashboard = lazy(() => import("./pages/finance/ARDashboard"));
const APDashboard = lazy(() => import("./pages/finance/APDashboard"));
const BalanceSheet = lazy(() => import("./pages/finance/BalanceSheet"));
const CashFlowStatement = lazy(() => import("./pages/finance/CashFlowStatement"));
const TrialBalance = lazy(() => import("./pages/finance/TrialBalance"));
const CustomerStatements = lazy(() => import("./pages/finance/CustomerStatements"));
const BatchVendorPayments = lazy(() => import("./pages/finance/BatchVendorPayments"));
const BankReconciliation = lazy(() => import("./pages/finance/BankReconciliation"));
const RatioAnalysis = lazy(() => import("./pages/finance/RatioAnalysis"));
const ExpenseApprovals = lazy(() => import("./pages/finance/ExpenseApprovals"));
const FinancialForecasting = lazy(() => import("./pages/finance/FinancialForecasting"));
const BudgetVsActual = lazy(() => import("./pages/finance/BudgetVsActual"));
const ReportBuilder = lazy(() => import("./pages/finance/ReportBuilder"));
const ReportScheduler = lazy(() => import("./pages/finance/ReportScheduler"));
const PLStatement = lazy(() => import("./pages/finance/PLStatement"));
const RecurringInvoices = lazy(() => import("./pages/finance/RecurringInvoices"));
const TaxFiling = lazy(() => import("./pages/finance/TaxFiling"));
const CurrencyCalculator = lazy(() => import("./pages/finance/CurrencyCalculator"));
const CostCenters = lazy(() => import("./pages/finance/CostCenters"));
const DeferredRevenue = lazy(() => import("./pages/finance/DeferredRevenue"));
const ConsolidatedReports = lazy(() => import("./pages/finance/ConsolidatedReports"));

// HR Pages - Additional
const PerformanceGoals = lazy(() => import("./pages/hr/PerformanceGoals"));
const ThreeSixtyFeedback = lazy(() => import("./pages/hr/ThreeSixtyFeedback"));
const SuccessionPlanning = lazy(() => import("./pages/hr/SuccessionPlanning"));
const ESSPortal = lazy(() => import("./pages/hr/ESSPortal"));
const GrievanceManagement = lazy(() => import("./pages/hr/GrievanceManagement"));

// Dashboard Pages
const Notifications = lazy(() => import("./pages/dashboard/Notifications"));
const ActivityFeed = lazy(() => import("./pages/dashboard/ActivityFeed"));
const ApprovalQueue = lazy(() => import("./pages/dashboard/ApprovalQueue"));
const ReportsOverview = lazy(() => import("./pages/dashboard/ReportsOverview"));
const WidgetBuilder = lazy(() => import("./pages/dashboard/WidgetBuilder"));

// CRM Pages
const LeadManagement = lazy(() => import("./pages/crm/LeadManagement"));
const Opportunities = lazy(() => import("./pages/crm/Opportunities"));
const QuotationBuilder = lazy(() => import("./pages/crm/QuotationBuilder"));
const CustomerProfile = lazy(() => import("./pages/crm/CustomerProfile"));
const SalesPipeline = lazy(() => import("./pages/crm/SalesPipeline"));
const SalesOrders = lazy(() => import("./pages/crm/SalesOrders"));
const LeadScoringConfig = lazy(() => import("./pages/crm/LeadScoringConfig"));
const LeadAnalytics = lazy(() => import("./pages/crm/LeadAnalytics"));
const OpportunityAnalytics = lazy(() => import("./pages/crm/OpportunityAnalytics"));
const SalesForecast = lazy(() => import("./pages/crm/SalesForecast"));
const ContactManagement = lazy(() => import("./pages/crm/ContactManagement"));
const LeadConversion = lazy(() => import("./pages/crm/LeadConversion"));
const CustomerSegmentation = lazy(() => import("./pages/crm/CustomerSegmentation"));
const InteractionLogging = lazy(() => import("./pages/crm/InteractionLogging"));
const SalesReports = lazy(() => import("./pages/crm/SalesReports"));
const SalesTerritory = lazy(() => import("./pages/crm/SalesTerritory"));
const CommissionTracking = lazy(() => import("./pages/crm/CommissionTracking"));
const ActivityManagement = lazy(() => import("./pages/crm/ActivityManagement"));
const EmailCampaigns = lazy(() => import("./pages/crm/EmailCampaigns"));
const CallCenterIntegration = lazy(() => import("./pages/crm/CallCenterIntegration"));
const LeadSources = lazy(() => import("./pages/crm/LeadSources"));
const CompetitorTracking = lazy(() => import("./pages/crm/CompetitorTracking"));
const CallerAnalyticsDashboard = lazy(() => import("./pages/crm/CallerAnalyticsDashboard"));
const EnterpriseLeadDetail = lazy(() => import("./pages/crm/EnterpriseLeadDetail"));
const EnterpriseLeadList = lazy(() => import("./pages/crm/EnterpriseLeadList"));
const CRMAnalyticsDashboard = lazy(() => import("./pages/crm/CRMAnalyticsDashboard"));
const AccountDetail = lazy(() => import("./pages/crm/AccountDetail"));
const DealDetail = lazy(() => import("./pages/crm/DealDetail"));
const CRMReports = lazy(() => import("./pages/crm/CRMReports"));
const CRMActivityCalendar = lazy(() => import("./pages/crm/CRMActivityCalendar"));
const InternManagement = lazy(() => import("./pages/crm/InternManagement"));
const InternDailyUpdate = lazy(() => import("./pages/crm/InternDailyUpdate"));
const InternSupervisorDashboard = lazy(() => import("./pages/crm/InternSupervisorDashboard"));
const LeadCategorySheets = lazy(() => import("./pages/crm/LeadCategorySheets"));
const CallerDailyProgress = lazy(() => import("./pages/crm/CallerDailyProgress"));
const CallHistoryPage = lazy(() => import("./pages/crm/CallHistoryPage"));
const EnterpriseDeals = lazy(() => import("./pages/crm/EnterpriseDeals"));
const DealDetailPage = lazy(() => import("./pages/crm/DealDetailPage"));
const EnterpriseOrders = lazy(() => import("./pages/crm/EnterpriseOrders"));
const OrderDetail = lazy(() => import("./pages/crm/OrderDetail"));
const ServicesList = lazy(() => import("./pages/crm/ServicesList"));
const ServiceDetailPage = lazy(() => import("./pages/crm/ServiceDetailPage"));
const QuotesList = lazy(() => import("./pages/crm/QuotesList"));
const ProfitabilityDashboard = lazy(() => import("./pages/crm/ProfitabilityDashboard"));
const QuoteDetailPage = lazy(() => import("./pages/crm/QuoteDetailPage"));

const PurchaseOrders = lazy(() => import("./pages/procurement/PurchaseOrders"));
const GoodsReceipt = lazy(() => import("./pages/procurement/GoodsReceipt"));
const ProcurementDashboard = lazy(() => import("./pages/procurement/ProcurementDashboard"));
const PurchaseRequisitions = lazy(() => import("./pages/procurement/PurchaseRequisitions"));
const RFQManagement = lazy(() => import("./pages/procurement/RFQManagement"));
const VendorPerformance = lazy(() => import("./pages/procurement/VendorPerformance"));
const ThreeWayMatch = lazy(() => import("./pages/procurement/ThreeWayMatch"));
const VendorContracts = lazy(() => import("./pages/procurement/VendorContracts"));
const VendorRatings = lazy(() => import("./pages/procurement/VendorRatings"));
const PurchaseAnalytics = lazy(() => import("./pages/procurement/PurchaseAnalytics"));
const SupplierPortal = lazy(() => import("./pages/procurement/SupplierPortal"));
const PurchaseApprovals = lazy(() => import("./pages/procurement/PurchaseApprovals"));
const ReturnToVendor = lazy(() => import("./pages/procurement/ReturnToVendor"));
const VendorOnboarding = lazy(() => import("./pages/procurement/VendorOnboarding"));
const PurchaseBudget = lazy(() => import("./pages/procurement/PurchaseBudget"));
const SourcingEvents = lazy(() => import("./pages/procurement/SourcingEvents"));

// Inventory Pages
const Products = lazy(() => import("./pages/inventory/Products"));
const StockLevels = lazy(() => import("./pages/inventory/StockLevels"));
const InventoryDashboard = lazy(() => import("./pages/inventory/InventoryDashboard"));
const ProductCategories = lazy(() => import("./pages/inventory/ProductCategories"));
const Warehouses = lazy(() => import("./pages/inventory/Warehouses"));
const WarehouseLocations = lazy(() => import("./pages/inventory/WarehouseLocations"));
const StockReceipts = lazy(() => import("./pages/inventory/StockReceipts"));
const StockIssues = lazy(() => import("./pages/inventory/StockIssues"));
const StockTransfers = lazy(() => import("./pages/inventory/StockTransfers"));
const InventoryAdjustments = lazy(() => import("./pages/inventory/InventoryAdjustments"));
const StockCount = lazy(() => import("./pages/inventory/StockCount"));
const BatchTracking = lazy(() => import("./pages/inventory/BatchTracking"));
const ReorderPoints = lazy(() => import("./pages/inventory/ReorderPoints"));
const InventoryValuation = lazy(() => import("./pages/inventory/InventoryValuation"));
const StockAging = lazy(() => import("./pages/inventory/StockAging"));
const DeadStock = lazy(() => import("./pages/inventory/DeadStock"));
const ABCAnalysis = lazy(() => import("./pages/inventory/ABCAnalysis"));
const InventoryForecasting = lazy(() => import("./pages/inventory/InventoryForecasting"));
const BinManagement = lazy(() => import("./pages/inventory/BinManagement"));

// Manufacturing Pages
const BOMCreator = lazy(() => import("./pages/manufacturing/BOMCreator"));
const WorkOrders = lazy(() => import("./pages/manufacturing/WorkOrders"));
const ManufacturingDashboard = lazy(() => import("./pages/manufacturing/ManufacturingDashboard"));
const ProductionSchedule = lazy(() => import("./pages/manufacturing/ProductionSchedule"));
const QualityChecks = lazy(() => import("./pages/manufacturing/QualityChecks"));
const WorkCenters = lazy(() => import("./pages/manufacturing/WorkCenters"));
const RoutingManagement = lazy(() => import("./pages/manufacturing/RoutingManagement"));
const MaterialConsumption = lazy(() => import("./pages/manufacturing/MaterialConsumption"));
const ProductionReports = lazy(() => import("./pages/manufacturing/ProductionReports"));
const DefectTracking = lazy(() => import("./pages/manufacturing/DefectTracking"));
const CapacityPlanning = lazy(() => import("./pages/manufacturing/CapacityPlanning"));
const ResourceAllocation = lazy(() => import("./pages/manufacturing/ResourceAllocation"));
const ProductionCosting = lazy(() => import("./pages/manufacturing/ProductionCosting"));
const MaintenanceScheduling = lazy(() => import("./pages/manufacturing/MaintenanceScheduling"));
const EquipmentManagement = lazy(() => import("./pages/manufacturing/EquipmentManagement"));
const ProductionPlanning = lazy(() => import("./pages/manufacturing/ProductionPlanning"));
const ShiftPlanning = lazy(() => import("./pages/manufacturing/ShiftPlanning"));
const ProductionDashboardWidgets = lazy(() => import("./pages/manufacturing/ProductionDashboardWidgets"));
const OEEDashboard = lazy(() => import("./pages/manufacturing/OEEDashboard"));
const MRPEngine = lazy(() => import("./pages/manufacturing/MRPEngine"));
const BatchTraceability = lazy(() => import("./pages/manufacturing/BatchTraceability"));

// Logistics Pages
const ShipmentTracking = lazy(() => import("./pages/logistics/ShipmentTracking"));
const LogisticsDashboard = lazy(() => import("./pages/logistics/LogisticsDashboard"));
const CarrierManagement = lazy(() => import("./pages/logistics/CarrierManagement"));
const DeliveryScheduling = lazy(() => import("./pages/logistics/DeliveryScheduling"));
const RouteOptimization = lazy(() => import("./pages/logistics/RouteOptimization"));
const FleetManagement = lazy(() => import("./pages/logistics/FleetManagement"));
const FreightManagement = lazy(() => import("./pages/logistics/FreightManagement"));
const ProofOfDelivery = lazy(() => import("./pages/logistics/ProofOfDelivery"));
const WarehouseOperationsDashboard = lazy(() => import("./pages/logistics/WarehouseOperationsDashboard"));
const CrossDockingManagement = lazy(() => import("./pages/logistics/CrossDockingManagement"));
const LastMileDelivery = lazy(() => import("./pages/logistics/LastMileDelivery"));
const ReturnsProcessing = lazy(() => import("./pages/logistics/ReturnsProcessing"));
const DriverAssignment = lazy(() => import("./pages/logistics/DriverAssignment"));
const FuelManagement = lazy(() => import("./pages/logistics/FuelManagement"));
const DeliverySLA = lazy(() => import("./pages/logistics/DeliverySLA"));

// Analytics Pages
const DashboardBuilder = lazy(() => import("./pages/analytics/DashboardBuilder"));
const CustomReportBuilder = lazy(() => import("./pages/analytics/CustomReportBuilder"));
const KPIDefinition = lazy(() => import("./pages/analytics/KPIDefinition"));
const KPITracking = lazy(() => import("./pages/analytics/KPITracking"));
const DataVisualization = lazy(() => import("./pages/analytics/DataVisualization"));
const ExecutiveReports = lazy(() => import("./pages/analytics/ExecutiveReports"));
const DrillDownReports = lazy(() => import("./pages/analytics/DrillDownReports"));
const TrendAnalysis = lazy(() => import("./pages/analytics/TrendAnalysis"));
const PredictiveAnalytics = lazy(() => import("./pages/analytics/PredictiveAnalytics"));
const ReportTemplates = lazy(() => import("./pages/analytics/ReportTemplates"));
const DataExport = lazy(() => import("./pages/analytics/DataExport"));
const ScheduledReports = lazy(() => import("./pages/analytics/ScheduledReports"));
const AnomalyDetection = lazy(() => import("./pages/analytics/AnomalyDetection"));
const CohortAnalysis = lazy(() => import("./pages/analytics/CohortAnalysis"));
const FunnelAnalysis = lazy(() => import("./pages/analytics/FunnelAnalysis"));
const RealTimeMonitoring = lazy(() => import("./pages/analytics/RealTimeMonitoring"));
const BenchmarkComparison = lazy(() => import("./pages/analytics/BenchmarkComparison"));
const AIInsights = lazy(() => import("./pages/analytics/AIInsights"));

// CRM Additional Pages
const CustomerJourneyMapping = lazy(() => import("./pages/crm/CustomerJourneyMapping"));
const SurveyNPS = lazy(() => import("./pages/crm/SurveyNPS"));
const ReferralTracking = lazy(() => import("./pages/crm/ReferralTracking"));
const LoyaltyProgram = lazy(() => import("./pages/crm/LoyaltyProgram"));

// Admin Additional Pages
const BulkUserImportExport = lazy(() => import("./pages/admin/BulkUserImportExport"));
const LicenseManagement = lazy(() => import("./pages/admin/LicenseManagement"));
const FeatureFlags = lazy(() => import("./pages/admin/FeatureFlags"));
const MaintenanceMode = lazy(() => import("./pages/admin/MaintenanceMode"));
const WhiteLabelConfig = lazy(() => import("./pages/admin/WhiteLabelConfig"));
const RolePermissionsGuide = lazy(() => import("./pages/admin/RolePermissionsGuide"));
const MobileDashboard = lazy(() => import("./pages/mobile/MobileDashboard"));
const OfflineDataSync = lazy(() => import("./pages/mobile/OfflineDataSync"));
const FieldOperations = lazy(() => import("./pages/mobile/FieldOperations"));
const MobileAttendance = lazy(() => import("./pages/mobile/MobileAttendance"));
const MobileExpenses = lazy(() => import("./pages/mobile/MobileExpenses"));
const MobileApprovals = lazy(() => import("./pages/mobile/MobileApprovals"));
const LocationTracking = lazy(() => import("./pages/mobile/LocationTracking"));
const MobileReports = lazy(() => import("./pages/mobile/MobileReports"));
const PushNotificationSettings = lazy(() => import("./pages/mobile/PushNotificationSettings"));
const MobileSalesEntry = lazy(() => import("./pages/mobile/MobileSalesEntry"));
const MobileInventoryCheck = lazy(() => import("./pages/mobile/MobileInventoryCheck"));
const MobileTimeEntry = lazy(() => import("./pages/mobile/MobileTimeEntry"));

// Admin Pages
const UserManagement = lazy(() => import("./pages/admin/UserManagement"));
const RoleManagement = lazy(() => import("./pages/admin/RoleManagement"));
const PermissionMatrix = lazy(() => import("./pages/admin/PermissionMatrix"));
const EmailTemplates = lazy(() => import("./pages/admin/EmailTemplates"));
const WorkflowBuilder = lazy(() => import("./pages/admin/WorkflowBuilderPage"));
const APIManagement = lazy(() => import("./pages/admin/APIManagementPage"));
const IntegrationsSetup = lazy(() => import("./pages/admin/IntegrationsSetup"));
const SystemLogs = lazy(() => import("./pages/admin/SystemLogs"));
const BackupRestore = lazy(() => import("./pages/admin/BackupRestore"));
const DataImportExport = lazy(() => import("./pages/admin/DataImportExport"));
const NotificationSettings = lazy(() => import("./pages/admin/NotificationSettings"));
const SecuritySettings = lazy(() => import("./pages/admin/SecuritySettings"));
const CompanySettings = lazy(() => import("./pages/admin/CompanySettings"));
const BranchManagement = lazy(() => import("./pages/admin/BranchManagement"));
const ApprovalWorkflows = lazy(() => import("./pages/admin/ApprovalWorkflows"));
const AuditSettings = lazy(() => import("./pages/admin/AuditSettings"));
const TaxSettings = lazy(() => import("./pages/admin/TaxSettings"));
const CurrencyManagement = lazy(() => import("./pages/admin/CurrencyManagement"));
const NumberingSequences = lazy(() => import("./pages/admin/NumberingSequences"));
const PluginMarketplace = lazy(() => import("./pages/admin/PluginMarketplace"));
const RolePermissionSimulator = lazy(() => import("./pages/admin/RolePermissionSimulator"));

// Healthcare Pages
const PatientRecords = lazy(() => import("./pages/healthcare/PatientRecords"));
const AppointmentScheduling = lazy(() => import("./pages/healthcare/AppointmentScheduling"));
const PrescriptionManagement = lazy(() => import("./pages/healthcare/PrescriptionManagement"));
const WardManagement = lazy(() => import("./pages/healthcare/WardManagement"));

// Security Pages
const LoginAnomalyDetection = lazy(() => import("./pages/admin/LoginAnomalyDetection"));

// New Enterprise Pages
const DocumentManagement = lazy(() => import("./pages/admin/DocumentManagement"));
const ComplianceTracker = lazy(() => import("./pages/admin/ComplianceTracker"));
const FixedAssets = lazy(() => import("./pages/finance/FixedAssets"));
const EmployeeDocuments = lazy(() => import("./pages/hr/EmployeeDocuments"));
const ServiceTickets = lazy(() => import("./pages/crm/ServiceTickets"));
const ContractManagement = lazy(() => import("./pages/procurement/ContractManagement"));
const ExpenseClaims = lazy(() => import("./pages/finance/ExpenseClaims"));
const VendorPortalDashboard = lazy(() => import("./pages/procurement/VendorPortalDashboard"));
const FleetMaintenanceLog = lazy(() => import("./pages/logistics/FleetMaintenanceLog"));
const QualityManagement = lazy(() => import("./pages/manufacturing/QualityManagement"));
const ShiftScheduling = lazy(() => import("./pages/hr/ShiftRoster"));
const CustomerFeedback = lazy(() => import("./pages/crm/CustomerFeedback"));
const WarehouseZones = lazy(() => import("./pages/inventory/WarehouseZones"));
const WebhookManager = lazy(() => import("./pages/admin/WebhookManager"));
const TrainingCourses = lazy(() => import("./pages/hr/TrainingCourses"));
const KPIScorecards = lazy(() => import("./pages/admin/KPIScorecards"));
const VendorRating = lazy(() => import("./pages/procurement/VendorRating"));
const WorkOrderManagement = lazy(() => import("./pages/manufacturing/WorkOrderManagement"));
const EmployeeGrievance = lazy(() => import("./pages/hr/EmployeeGrievance"));
const CreditNotes = lazy(() => import("./pages/finance/CreditNotes"));
const DeliveryRoutePlanner = lazy(() => import("./pages/logistics/DeliveryRoutePlanner"));
const MachineDowntimeTracker = lazy(() => import("./pages/manufacturing/MachineDowntimeTracker"));
const AssetDepreciation = lazy(() => import("./pages/finance/AssetDepreciation"));
const ExitInterview = lazy(() => import("./pages/hr/ExitInterview"));
const ContractRenewalTracker = lazy(() => import("./pages/procurement/ContractRenewalTracker"));
const DemandPlanning = lazy(() => import("./pages/inventory/DemandPlanning"));
const ComplianceAuditLog = lazy(() => import("./pages/admin/ComplianceAuditLog"));
const LearningDevelopment = lazy(() => import("./pages/hr/LearningDevelopment"));
const ProfitCenters = lazy(() => import("./pages/finance/ProfitCenters"));
const EquipmentLifecycle = lazy(() => import("./pages/manufacturing/EquipmentLifecycle"));
const ShipmentConsolidation = lazy(() => import("./pages/logistics/ShipmentConsolidation"));
const MarketingCampaigns = lazy(() => import("./pages/crm/MarketingCampaigns"));
const WorkforceAnalytics = lazy(() => import("./pages/hr/WorkforceAnalytics"));
const IntercompanyTransactions = lazy(() => import("./pages/finance/IntercompanyTransactions"));
const VendorScorecard = lazy(() => import("./pages/procurement/VendorScorecard"));
const InventoryAuditTrail = lazy(() => import("./pages/inventory/InventoryAuditTrail"));
const ProductionYieldAnalysis = lazy(() => import("./pages/manufacturing/ProductionYieldAnalysis"));
const TaxComplianceDashboard = lazy(() => import("./pages/finance/TaxComplianceDashboard"));
const ESSRequests = lazy(() => import("./pages/hr/ESSRequests"));
const SupplierRiskAssessment = lazy(() => import("./pages/procurement/SupplierRiskAssessment"));
const WarehouseCapacityPlanner = lazy(() => import("./pages/inventory/WarehouseCapacityPlanner"));
const ContractLifecycle = lazy(() => import("./pages/operations/ContractLifecycle"));
const ShiftRoster = lazy(() => import("./pages/hr/ShiftRoster"));
const QualityControl = lazy(() => import("./pages/manufacturing/QualityControl"));
const DeliveryTracking = lazy(() => import("./pages/logistics/DeliveryTracking"));
const CustomReports = lazy(() => import("./pages/reports/CustomReports"));

// Retail Pages
const POSTerminal = lazy(() => import("./pages/retail/POSTerminal"));
const LoyaltyPromotions = lazy(() => import("./pages/retail/LoyaltyPromotions"));

// Enterprise Infra Pages
const WorkflowBuilderPage = lazy(() => import("./pages/admin/WorkflowBuilderPage"));
const CustomFieldsManager = lazy(() => import("./pages/admin/CustomFieldsManager"));
const SessionManagementPage = lazy(() => import("./pages/admin/SessionManagementPage"));
const APIManagementPage = lazy(() => import("./pages/admin/APIManagementPage"));

const SalesDashboard = lazy(() => import("./pages/sales/SalesDashboard"));
const OrderProcessing = lazy(() => import("./pages/sales/OrderProcessing"));
const OrderFulfillment = lazy(() => import("./pages/sales/OrderFulfillment"));
const OrderTracking = lazy(() => import("./pages/sales/OrderTracking"));
const ReturnsManagement = lazy(() => import("./pages/sales/ReturnsManagement"));
const SalesTargets = lazy(() => import("./pages/sales/SalesTargets"));
const PricingRules = lazy(() => import("./pages/sales/PricingRules"));
const QuoteConversion = lazy(() => import("./pages/sales/QuoteConversion"));
const DeliveryNotes = lazy(() => import("./pages/sales/DeliveryNotes"));
const PriceLists = lazy(() => import("./pages/sales/PriceLists"));
const DiscountsManagement = lazy(() => import("./pages/sales/DiscountsManagement"));
const TaxConfiguration = lazy(() => import("./pages/sales/TaxConfiguration"));
const ShippingMethods = lazy(() => import("./pages/sales/ShippingMethods"));
const PaymentTerms = lazy(() => import("./pages/sales/PaymentTerms"));
const OrderTemplates = lazy(() => import("./pages/sales/OrderTemplates"));
const BulkOrders = lazy(() => import("./pages/sales/BulkOrders"));
const SalesAnalytics = lazy(() => import("./pages/sales/SalesAnalytics"));

// Portal pages
const PortalLogin = lazy(() => import("./pages/portal/PortalLogin"));
const PortalDashboard = lazy(() => import("./pages/portal/PortalDashboard"));
const PortalInvoices = lazy(() => import("./pages/portal/PortalInvoices"));
const PortalPayments = lazy(() => import("./pages/portal/PortalPayments"));
const PortalProjects = lazy(() => import("./pages/portal/PortalProjects"));
const PortalDeliverables = lazy(() => import("./pages/portal/PortalDeliverables"));
const PortalMessages = lazy(() => import("./pages/portal/PortalMessages"));
const AcceptInvite = lazy(() => import("./pages/portal/AcceptInvite"));
const PortalResetPassword = lazy(() => import("./pages/portal/ResetPassword"));
const PortalSettings = lazy(() => import("./pages/portal/PortalSettings"));
const PortalDocumentCenter = lazy(() => import("./pages/portal/PortalDocumentCenter"));
const PortalSupportTickets = lazy(() => import("./pages/portal/PortalSupportTickets"));

// Auth Pages
const TwoFactorSetup = lazy(() => import("./pages/auth/TwoFactorSetup"));
const OrganizationSetupWizard = lazy(() => import("./pages/auth/OrganizationSetupWizard"));
const MultiOrgSelector = lazy(() => import("./pages/auth/MultiOrgSelector"));
const SessionManagement = lazy(() => import("./pages/auth/SessionManagement"));
const FirstTimeUserTour = lazy(() => import("./pages/auth/FirstTimeUserTour"));

// Super Admin Pages
const SuperAdminLogin = lazy(() => import("./pages/superadmin/Login"));
const SuperAdminDashboard = lazy(() => import("./pages/superadmin/Dashboard"));
const ManageWebsite = lazy(() => import("./pages/superadmin/ManageWebsite"));
const CreateBusiness = lazy(() => import("./pages/superadmin/CreateBusiness"));
const AllBusinesses = lazy(() => import("./pages/superadmin/AllBusinesses"));
const BusinessDetail = lazy(() => import("./pages/superadmin/BusinessDetail"));
const SuperAdminAnalytics = lazy(() => import("./pages/superadmin/Analytics"));
const SuperAdminSettings = lazy(() => import("./pages/superadmin/Settings"));
const PlatformAuditLogs = lazy(() => import("./pages/superadmin/AuditLogs"));
const PricingPlans = lazy(() => import("./pages/superadmin/PricingPlans"));
const OffersDiscounts = lazy(() => import("./pages/superadmin/OffersDiscounts"));
const TrialDashboard = lazy(() => import("./pages/superadmin/TrialDashboard"));
const SuperAdminApprovalQueue = lazy(() => import("./pages/superadmin/ApprovalQueue"));
const PaymentHistory = lazy(() => import("./pages/superadmin/PaymentHistory"));
const ContactInquiries = lazy(() => import("./pages/superadmin/ContactInquiries"));
const SuperAdminUsers = lazy(() => import("./pages/superadmin/Users"));
const FeatureControlCenter = lazy(() => import("./pages/superadmin/FeatureControlCenter"));

// Detail Pages
const LeadDetail = lazy(() => import("./pages/crm/LeadDetail"));
const EmployeePayrollDetail = lazy(() => import("./pages/hr/EmployeePayrollDetail"));

// Error Pages
const Unauthorized = lazy(() => import("./pages/errors/Unauthorized"));
const Forbidden = lazy(() => import("./pages/errors/Forbidden"));

// Business Admin Pages
const BusinessLogin = lazy(() => import("./pages/business/Login"));
const BusinessSetup = lazy(() => import("./pages/business/Setup"));
const InviteEmployee = lazy(() => import("./pages/business/InviteEmployee"));
const SetupRedirect = lazy(() => import("./pages/SetupRedirect"));

// Employee Pages
const EmployeeLogin = lazy(() => import("./pages/employee/Login"));
const EmployeeSetPassword = lazy(() => import("./pages/employee/SetPassword"));

// Dev Pages
const DataIsolationTest = lazy(() => import("./pages/dev/DataIsolationTest"));

// Phase 7 Components
const AdvancedReportBuilder = lazy(() => import("./components/analytics/AdvancedReportBuilder"));
const WorkflowAutomationEngine = lazy(() => import("./components/automation/WorkflowAutomationEngine"));
const ClientSelfService = lazy(() => import("./components/portal/ClientSelfService"));
const AIInsightsHub = lazy(() => import("./components/ai/AIInsightsHub"));

// Phase 8 Components
const DocumentManagementSystem = lazy(() => import("./components/documents/DocumentManagementSystem"));
const MultiCurrencyLocalization = lazy(() => import("./components/localization/MultiCurrencyLocalization"));
const AdvancedSecurityCompliance = lazy(() => import("./components/security/AdvancedSecurityCompliance"));
const IntegrationHub = lazy(() => import("./components/integrations/IntegrationHub"));

// Phase 9 Components
const EmployeeExperiencePlatform = lazy(() => import("./components/hr/EmployeeExperiencePlatform"));
const SupplyChainIntelligence = lazy(() => import("./components/inventory/SupplyChainIntelligence"));
const CustomFormsWorkflows = lazy(() => import("./components/forms/CustomFormsWorkflows"));
const PerformanceCommandCenter = lazy(() => import("./components/performance/PerformanceCommandCenter"));

// Phase 10 Components
const AdvancedReportingEngine = lazy(() => import("./components/reporting/AdvancedReportingEngine"));
const CommunicationHub = lazy(() => import("./components/communication/CommunicationHub"));
const AssetFacilityManagement = lazy(() => import("./components/assets/AssetFacilityManagement"));

// Layout components - lazy loaded for performance
const DashboardLayout = lazy(() => import("./components/layout/DashboardLayout"));
const PortalLayout = lazy(() => import("./pages/portal/PortalLayout"));

import { toast } from 'sonner';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
    mutations: {
      onError: (error: Error) => {
        const msg = error.message || 'Something went wrong';
        if (msg.includes('JWT') || msg.includes('session')) {
          toast.error('Session expired. Please log in again.');
        } else if (msg.includes('User limit reached')) {
          toast.error(msg);
        } else {
          toast.error(msg.length > 120 ? 'Operation failed. Please try again.' : msg);
        }
      },
    },
  },
});

// Loading fallback component - minimal for fast paint
const PageLoader = forwardRef<HTMLDivElement>((_, ref) => (
  <div ref={ref} className="min-h-screen flex items-center justify-center bg-background">
    <div className="flex flex-col items-center gap-2">
      <div className="h-6 w-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  </div>
));
PageLoader.displayName = "PageLoader";

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Sonner />
        <BrowserRouter>
          <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:z-[100] focus:top-2 focus:left-2 focus:bg-primary focus:text-primary-foreground focus:px-4 focus:py-2 focus:rounded-lg">
            Skip to main content
          </a>
          <ErrorBoundary>
            <Suspense fallback={<PageLoader />}>
              <main id="main-content">
                <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/features" element={<Features />} />
              <Route path="/industries" element={<Industries />} />
              <Route path="/about" element={<About />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/security" element={<Security />} />
              <Route path="/auth" element={<Auth />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/auth/2fa-setup" element={<TwoFactorSetup />} />
              <Route path="/auth/organization-setup" element={<OrganizationSetupWizard />} />
              <Route path="/auth/select-organization" element={<MultiOrgSelector />} />
              <Route path="/auth/sessions" element={<SessionManagement />} />
              <Route path="/auth/tour" element={<FirstTimeUserTour />} />
              
              <Route element={<DashboardLayout />}>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/dashboard/notifications" element={<Notifications />} />
                <Route path="/dashboard/activity" element={<ActivityFeed />} />
                <Route path="/dashboard/approvals" element={<ApprovalQueue />} />
                <Route path="/dashboard/reports" element={<ReportsOverview />} />
                <Route path="/dashboard/widgets" element={<WidgetBuilder />} />
                <Route path="/sheets" element={<Sheets />} />
                <Route path="/daily-report" element={<DailyReport />} />
                <Route path="/analytics" element={<Analytics />} />
                <Route path="/finance" element={<FinanceDashboard />} />
                <Route path="/finance/chart-of-accounts" element={<ChartOfAccounts />} />
                <Route path="/finance/journal-entries" element={<JournalEntries />} />
                <Route path="/finance/vendors" element={<Vendors />} />
                <Route path="/finance/budgets" element={<Budgets />} />
                <Route path="/tasks" element={<Tasks />} />
                <Route path="/time-tracking" element={<TimeTracking />} />
                <Route path="/attendance" element={<Attendance />} />
                <Route path="/payroll" element={<Payroll />} />
                <Route path="/projects" element={<Projects />} />
                <Route path="/clients" element={<Clients />} />
                <Route path="/expenses" element={<Expenses />} />
                <Route path="/follow-ups" element={<FollowUps />} />
                <Route path="/approvals" element={<ApprovalCenter />} />
                <Route path="/command-center" element={<CommandCenter />} />
                <Route path="/documents" element={<DocumentManager />} />
                <Route path="/compliance" element={<ComplianceCenter />} />
                <Route path="/agile" element={<AgileBoard />} />
                <Route path="/communication-automation" element={<CommunicationAutomation />} />
                <Route path="/inventory-intelligence" element={<InventoryIntelligence />} />
                <Route path="/hr-performance" element={<HRPerformanceSuite />} />
                <Route path="/report-builder" element={<AdvancedReportBuilder />} />
                <Route path="/workflow-automation" element={<WorkflowAutomationEngine />} />
                <Route path="/client-self-service" element={<ClientSelfService />} />
                <Route path="/ai-insights" element={<AIInsightsHub />} />
                <Route path="/document-management" element={<DocumentManagementSystem />} />
                <Route path="/multi-currency" element={<MultiCurrencyLocalization />} />
                <Route path="/security-compliance" element={<AdvancedSecurityCompliance />} />
                <Route path="/integration-hub" element={<IntegrationHub />} />
                <Route path="/employee-experience" element={<EmployeeExperiencePlatform />} />
                <Route path="/supply-chain" element={<SupplyChainIntelligence />} />
                <Route path="/custom-forms" element={<CustomFormsWorkflows />} />
                <Route path="/performance-center" element={<PerformanceCommandCenter />} />
                <Route path="/advanced-reports" element={<AdvancedReportingEngine />} />
                <Route path="/communication-hub" element={<CommunicationHub />} />
                <Route path="/asset-management" element={<AssetFacilityManagement />} />
                <Route path="/communications" element={<Communications />} />
                <Route path="/performance" element={<Performance />} />
                <Route path="/audit-logs" element={<AuditLogs />} />
                <Route path="/automations" element={<Automations />} />
                <Route path="/crm" element={<CRMDashboard />} />
                <Route path="/team" element={<Team />} />
                <Route path="/admin" element={<Admin />} />
                <Route path="/settings" element={<Settings />} />
                <Route path="/organization" element={<OrganizationSettings />} />
                <Route path="/caller-workstation" element={<CallerWorkstation />} />
                <Route path="/data-migration" element={<ExcelDataMigrationPage />} />
                
                {/* HR Routes */}
                <Route path="/hr/employee/:id" element={<EmployeeProfile />} />
                <Route path="/hr/add-employee" element={<AddEmployeeWizard />} />
                <Route path="/hr/leave-types" element={<LeaveTypes />} />
                <Route path="/hr/leave-approvals" element={<LeaveApprovals />} />
                <Route path="/hr/leave-calendar" element={<LeaveCalendar />} />
                <Route path="/hr/leave-balance" element={<LeaveBalance />} />
                <Route path="/hr/salary-components" element={<SalaryComponents />} />
                <Route path="/hr/salary-structures" element={<SalaryStructures />} />
                <Route path="/hr/payroll-processing" element={<PayrollProcessing />} />
                <Route path="/hr/loans" element={<LoanManagement />} />
                <Route path="/hr/job-openings" element={<JobOpenings />} />
                <Route path="/hr/job-applications" element={<JobApplications />} />
                <Route path="/hr/interviews" element={<InterviewManagement />} />
                <Route path="/hr/departments" element={<DepartmentManagement />} />
                <Route path="/hr/org-chart" element={<OrganizationChart />} />
                <Route path="/hr/leave-encashment" element={<LeaveEncashment />} />
                <Route path="/hr/bulk-import" element={<BulkEmployeeImport />} />
                <Route path="/hr/attendance-corrections" element={<AttendanceCorrections />} />
                <Route path="/hr/attendance-policies" element={<AttendancePolicies />} />
                <Route path="/hr/onboarding-checklist" element={<OnboardingChecklist />} />
                <Route path="/hr/offer-letters" element={<OfferLetterGenerator />} />
                <Route path="/hr/salaries" element={<EmployeeSalaryView />} />
                <Route path="/hr/payroll-history" element={<PayrollHistory />} />
                <Route path="/hr/employee-status" element={<EmployeeStatusManagement />} />
                <Route path="/hr/termination" element={<TerminationProcess />} />
                <Route path="/hr/benefits" element={<BenefitsAdministration />} />
                <Route path="/hr/training" element={<TrainingManagement />} />
                <Route path="/hr/skills-matrix" element={<SkillsMatrix />} />
                <Route path="/hr/performance-goals" element={<PerformanceGoals />} />
                <Route path="/hr/360-feedback" element={<ThreeSixtyFeedback />} />
                <Route path="/hr/succession-planning" element={<SuccessionPlanning />} />
                <Route path="/hr/ess-portal" element={<ESSPortal />} />
                <Route path="/hr/grievance" element={<GrievanceManagement />} />
                <Route path="/hr/payroll/:employeeId" element={<EmployeePayrollDetail />} />
                
                {/* Finance Routes */}
                <Route path="/finance/credit-notes" element={<CreditDebitNotes />} />
                <Route path="/finance/payments" element={<PaymentRecording />} />
                <Route path="/finance/bill-approvals" element={<BillApprovalQueue />} />
                <Route path="/finance/ledger" element={<AccountLedger />} />
                <Route path="/finance/invoices" element={<CustomerInvoices />} />
                <Route path="/finance/bills" element={<VendorBills />} />
                <Route path="/finance/ar" element={<ARDashboard />} />
                <Route path="/finance/ap" element={<APDashboard />} />
                <Route path="/finance/balance-sheet" element={<BalanceSheet />} />
                <Route path="/finance/cash-flow" element={<CashFlowStatement />} />
                <Route path="/finance/trial-balance" element={<TrialBalance />} />
                <Route path="/finance/statements" element={<CustomerStatements />} />
                <Route path="/finance/batch-payments" element={<BatchVendorPayments />} />
                <Route path="/finance/reconciliation" element={<BankReconciliation />} />
                <Route path="/finance/ratio-analysis" element={<RatioAnalysis />} />
                <Route path="/finance/expense-approvals" element={<ExpenseApprovals />} />
                <Route path="/finance/forecasting" element={<FinancialForecasting />} />
                <Route path="/finance/budget-vs-actual" element={<BudgetVsActual />} />
                <Route path="/finance/report-builder" element={<ReportBuilder />} />
                <Route path="/finance/report-scheduler" element={<ReportScheduler />} />
                <Route path="/finance/currency-calculator" element={<CurrencyCalculator />} />
                <Route path="/finance/pl-statement" element={<PLStatement />} />
                <Route path="/finance/recurring-invoices" element={<RecurringInvoices />} />
                <Route path="/finance/tax-filing" element={<TaxFiling />} />
                <Route path="/finance/cost-centers" element={<CostCenters />} />
                <Route path="/finance/deferred-revenue" element={<DeferredRevenue />} />
                <Route path="/finance/consolidated" element={<ConsolidatedReports />} />
                
                {/* CRM Routes */}
                <Route path="/crm/leads" element={<EnterpriseLeadList />} />
                <Route path="/crm/leads/:id" element={<EnterpriseLeadDetail />} />
                <Route path="/crm/lead/:id" element={<EnterpriseLeadDetail />} />
                <Route path="/crm/leads-old" element={<LeadManagement />} />
                <Route path="/crm/analytics" element={<CRMAnalyticsDashboard />} />
                <Route path="/crm/account/:id" element={<AccountDetail />} />
                <Route path="/crm/reports" element={<CRMReports />} />
                <Route path="/crm/calendar" element={<CRMActivityCalendar />} />
                <Route path="/crm/interns" element={<InternManagement />} />
                <Route path="/crm/intern-update" element={<InternDailyUpdate />} />
                <Route path="/crm/intern-dashboard" element={<InternSupervisorDashboard />} />
                <Route path="/crm/lead-sheets" element={<LeadCategorySheets />} />
                <Route path="/crm/caller-progress" element={<CallerDailyProgress />} />
                <Route path="/crm/quotations" element={<QuotationBuilder />} />
                <Route path="/crm/quotes" element={<QuotesList />} />
                <Route path="/crm/quote/:id" element={<QuoteDetailPage />} />
                <Route path="/crm/customer/:id" element={<CustomerProfile />} />
                <Route path="/crm/contact/:id" element={<CustomerProfile />} />
                <Route path="/crm/pipeline" element={<SalesPipeline />} />
                <Route path="/crm/deals" element={<EnterpriseDeals />} />
                <Route path="/crm/deal/:id" element={<DealDetailPage />} />
                <Route path="/crm/orders" element={<EnterpriseOrders />} />
                <Route path="/crm/order/:id" element={<OrderDetail />} />
                <Route path="/crm/services" element={<ServicesList />} />
                <Route path="/crm/service/:id" element={<ServiceDetailPage />} />
                <Route path="/crm/profitability" element={<ProfitabilityDashboard />} />
                <Route path="/crm/sales-orders" element={<SalesOrders />} />
                <Route path="/crm/lead-scoring" element={<LeadScoringConfig />} />
                <Route path="/crm/lead-analytics" element={<LeadAnalytics />} />
                <Route path="/crm/opportunity-analytics" element={<OpportunityAnalytics />} />
                <Route path="/crm/sales-forecast" element={<SalesForecast />} />
                <Route path="/crm/contacts" element={<ContactManagement />} />
                <Route path="/crm/lead-conversion" element={<LeadConversion />} />
                <Route path="/crm/segmentation" element={<CustomerSegmentation />} />
                <Route path="/crm/interactions" element={<InteractionLogging />} />
                <Route path="/crm/reports" element={<SalesReports />} />
                <Route path="/crm/territories" element={<SalesTerritory />} />
                <Route path="/crm/commissions" element={<CommissionTracking />} />
                <Route path="/crm/activities" element={<ActivityManagement />} />
                <Route path="/crm/email-campaigns" element={<EmailCampaigns />} />
                <Route path="/crm/call-center" element={<CallCenterIntegration />} />
                <Route path="/crm/lead-sources" element={<LeadSources />} />
                <Route path="/crm/competitors" element={<CompetitorTracking />} />
                <Route path="/crm/customer-journey" element={<CustomerJourneyMapping />} />
                <Route path="/crm/surveys" element={<SurveyNPS />} />
                <Route path="/crm/referrals" element={<ReferralTracking />} />
                <Route path="/crm/caller-analytics" element={<CallerAnalyticsDashboard />} />
                <Route path="/crm/call-logs" element={<CallHistoryPage />} />
                <Route path="/crm/loyalty" element={<LoyaltyProgram />} />
                
                {/* Procurement Routes */}
                <Route path="/procurement" element={<ProcurementDashboard />} />
                <Route path="/procurement/orders" element={<PurchaseOrders />} />
                <Route path="/procurement/receipts" element={<GoodsReceipt />} />
                <Route path="/procurement/requisitions" element={<PurchaseRequisitions />} />
                <Route path="/procurement/rfq" element={<RFQManagement />} />
                <Route path="/procurement/vendor-performance" element={<VendorPerformance />} />
                <Route path="/procurement/three-way-match" element={<ThreeWayMatch />} />
                <Route path="/procurement/contracts" element={<VendorContracts />} />
                <Route path="/procurement/ratings" element={<VendorRatings />} />
                <Route path="/procurement/analytics" element={<PurchaseAnalytics />} />
                <Route path="/procurement/supplier-portal" element={<SupplierPortal />} />
                <Route path="/procurement/approvals" element={<PurchaseApprovals />} />
                <Route path="/procurement/returns" element={<ReturnToVendor />} />
                <Route path="/procurement/vendor-onboarding" element={<VendorOnboarding />} />
                <Route path="/procurement/budget" element={<PurchaseBudget />} />
                <Route path="/procurement/sourcing" element={<SourcingEvents />} />
                
                {/* Inventory Routes */}
                <Route path="/inventory" element={<InventoryDashboard />} />
                <Route path="/inventory/products" element={<Products />} />
                <Route path="/inventory/stock" element={<StockLevels />} />
                <Route path="/inventory/categories" element={<ProductCategories />} />
                <Route path="/inventory/warehouses" element={<Warehouses />} />
                <Route path="/inventory/locations" element={<WarehouseLocations />} />
                <Route path="/inventory/receipts" element={<StockReceipts />} />
                <Route path="/inventory/issues" element={<StockIssues />} />
                <Route path="/inventory/transfers" element={<StockTransfers />} />
                <Route path="/inventory/adjustments" element={<InventoryAdjustments />} />
                <Route path="/inventory/stock-count" element={<StockCount />} />
                <Route path="/inventory/batch-tracking" element={<BatchTracking />} />
                <Route path="/inventory/reorder-points" element={<ReorderPoints />} />
                <Route path="/inventory/valuation" element={<InventoryValuation />} />
                <Route path="/inventory/aging" element={<StockAging />} />
                <Route path="/inventory/dead-stock" element={<DeadStock />} />
                <Route path="/inventory/abc-analysis" element={<ABCAnalysis />} />
                <Route path="/inventory/forecasting" element={<InventoryForecasting />} />
                <Route path="/inventory/bins" element={<BinManagement />} />
                
                {/* Manufacturing Routes */}
                <Route path="/manufacturing" element={<ManufacturingDashboard />} />
                <Route path="/manufacturing/bom" element={<BOMCreator />} />
                <Route path="/manufacturing/work-orders" element={<WorkOrders />} />
                <Route path="/manufacturing/schedule" element={<ProductionSchedule />} />
                <Route path="/manufacturing/quality-checks" element={<QualityChecks />} />
                <Route path="/manufacturing/work-centers" element={<WorkCenters />} />
                <Route path="/manufacturing/routing" element={<RoutingManagement />} />
                <Route path="/manufacturing/material-consumption" element={<MaterialConsumption />} />
                <Route path="/manufacturing/reports" element={<ProductionReports />} />
                <Route path="/manufacturing/defects" element={<DefectTracking />} />
                <Route path="/manufacturing/capacity-planning" element={<CapacityPlanning />} />
                <Route path="/manufacturing/resource-allocation" element={<ResourceAllocation />} />
                <Route path="/manufacturing/production-costing" element={<ProductionCosting />} />
                <Route path="/manufacturing/maintenance-scheduling" element={<MaintenanceScheduling />} />
                <Route path="/manufacturing/equipment" element={<EquipmentManagement />} />
                <Route path="/manufacturing/production-planning" element={<ProductionPlanning />} />
                <Route path="/manufacturing/shift-planning" element={<ShiftPlanning />} />
                <Route path="/manufacturing/dashboard-widgets" element={<ProductionDashboardWidgets />} />
                <Route path="/manufacturing/oee" element={<OEEDashboard />} />
                <Route path="/manufacturing/mrp" element={<MRPEngine />} />
                <Route path="/manufacturing/batch-traceability" element={<BatchTraceability />} />
                
                {/* Logistics Routes */}
                <Route path="/logistics" element={<LogisticsDashboard />} />
                <Route path="/logistics/shipments" element={<ShipmentTracking />} />
                <Route path="/logistics/carriers" element={<CarrierManagement />} />
                <Route path="/logistics/delivery-scheduling" element={<DeliveryScheduling />} />
                <Route path="/logistics/route-optimization" element={<RouteOptimization />} />
                <Route path="/logistics/fleet" element={<FleetManagement />} />
                <Route path="/logistics/freight" element={<FreightManagement />} />
                <Route path="/logistics/proof-of-delivery" element={<ProofOfDelivery />} />
                <Route path="/logistics/warehouse-operations" element={<WarehouseOperationsDashboard />} />
                <Route path="/logistics/cross-docking" element={<CrossDockingManagement />} />
                <Route path="/logistics/last-mile" element={<LastMileDelivery />} />
                <Route path="/logistics/returns" element={<ReturnsProcessing />} />
                <Route path="/logistics/driver-assignment" element={<DriverAssignment />} />
                <Route path="/logistics/fuel-management" element={<FuelManagement />} />
                <Route path="/logistics/sla" element={<DeliverySLA />} />
                
                {/* Analytics Routes */}
                <Route path="/analytics/dashboard-builder" element={<DashboardBuilder />} />
                <Route path="/analytics/report-builder" element={<CustomReportBuilder />} />
                <Route path="/analytics/kpi-definition" element={<KPIDefinition />} />
                <Route path="/analytics/kpi-tracking" element={<KPITracking />} />
                <Route path="/analytics/data-visualization" element={<DataVisualization />} />
                <Route path="/analytics/executive-reports" element={<ExecutiveReports />} />
                <Route path="/analytics/drill-down" element={<DrillDownReports />} />
                <Route path="/analytics/trends" element={<TrendAnalysis />} />
                <Route path="/analytics/predictive" element={<PredictiveAnalytics />} />
                <Route path="/analytics/templates" element={<ReportTemplates />} />
                <Route path="/analytics/export" element={<DataExport />} />
                <Route path="/analytics/scheduled-reports" element={<ScheduledReports />} />
                <Route path="/analytics/anomaly-detection" element={<AnomalyDetection />} />
                <Route path="/analytics/cohort-analysis" element={<CohortAnalysis />} />
                <Route path="/analytics/funnel-analysis" element={<FunnelAnalysis />} />
                <Route path="/analytics/real-time" element={<RealTimeMonitoring />} />
                <Route path="/analytics/benchmarks" element={<BenchmarkComparison />} />
                <Route path="/analytics/ai-insights" element={<AIInsights />} />
                
                {/* Admin Routes */}
                <Route path="/admin/users" element={<UserManagement />} />
                <Route path="/admin/roles" element={<RoleManagement />} />
                <Route path="/admin/permissions" element={<PermissionMatrix />} />
                <Route path="/admin/email-templates" element={<EmailTemplates />} />
                <Route path="/admin/workflow-builder" element={<WorkflowBuilder />} />
                <Route path="/admin/api" element={<APIManagement />} />
                <Route path="/admin/integrations" element={<IntegrationsSetup />} />
                <Route path="/admin/system-logs" element={<SystemLogs />} />
                <Route path="/admin/backup" element={<BackupRestore />} />
                <Route path="/admin/import-export" element={<DataImportExport />} />
                <Route path="/admin/notifications" element={<NotificationSettings />} />
                <Route path="/admin/security" element={<SecuritySettings />} />
                <Route path="/admin/company" element={<CompanySettings />} />
                <Route path="/admin/branches" element={<BranchManagement />} />
                <Route path="/admin/approval-workflows" element={<ApprovalWorkflows />} />
                <Route path="/admin/audit-settings" element={<AuditSettings />} />
                <Route path="/admin/tax-settings" element={<TaxSettings />} />
                <Route path="/admin/currencies" element={<CurrencyManagement />} />
                <Route path="/admin/numbering" element={<NumberingSequences />} />
                <Route path="/admin/bulk-users" element={<BulkUserImportExport />} />
                <Route path="/admin/licenses" element={<LicenseManagement />} />
                <Route path="/admin/feature-flags" element={<FeatureFlags />} />
                <Route path="/admin/maintenance" element={<MaintenanceMode />} />
                <Route path="/admin/white-label" element={<WhiteLabelConfig />} />
                <Route path="/admin/role-permissions-guide" element={<RolePermissionsGuide />} />
                <Route path="/admin/plugins" element={<PluginMarketplace />} />
                <Route path="/admin/permission-simulator" element={<RolePermissionSimulator />} />
                
                {/* Healthcare Routes */}
                <Route path="/healthcare/patients" element={<PatientRecords />} />
                <Route path="/healthcare/appointments" element={<AppointmentScheduling />} />
                <Route path="/healthcare/prescriptions" element={<PrescriptionManagement />} />
                <Route path="/healthcare/wards" element={<WardManagement />} />
                
                {/* Security Routes */}
                <Route path="/admin/documents" element={<DocumentManagement />} />
                <Route path="/admin/compliance" element={<ComplianceTracker />} />
                
                {/* New Finance Routes */}
                <Route path="/finance/fixed-assets" element={<FixedAssets />} />
                
                {/* New HR Routes */}
                <Route path="/hr/documents" element={<EmployeeDocuments />} />
                
                {/* New CRM Routes */}
                <Route path="/crm/service-tickets" element={<ServiceTickets />} />
                <Route path="/admin/login-anomalies" element={<LoginAnomalyDetection />} />
                
                {/* New Batch Routes */}
                <Route path="/procurement/contracts" element={<ContractManagement />} />
                <Route path="/procurement/vendor-portal" element={<VendorPortalDashboard />} />
                <Route path="/finance/expense-claims" element={<ExpenseClaims />} />
                <Route path="/logistics/fleet-maintenance" element={<FleetMaintenanceLog />} />
                <Route path="/manufacturing/quality" element={<QualityManagement />} />
                <Route path="/hr/shift-scheduling" element={<ShiftScheduling />} />
                <Route path="/crm/feedback" element={<CustomerFeedback />} />
                <Route path="/inventory/warehouse-zones" element={<WarehouseZones />} />
                <Route path="/admin/webhooks" element={<WebhookManager />} />
                <Route path="/hr/training" element={<TrainingCourses />} />
                <Route path="/admin/kpi-scorecards" element={<KPIScorecards />} />
                <Route path="/procurement/vendor-rating" element={<VendorRating />} />
                <Route path="/manufacturing/work-orders" element={<WorkOrderManagement />} />
                <Route path="/hr/grievance" element={<EmployeeGrievance />} />
                <Route path="/finance/credit-notes" element={<CreditNotes />} />
                <Route path="/logistics/route-planner" element={<DeliveryRoutePlanner />} />
                <Route path="/manufacturing/downtime" element={<MachineDowntimeTracker />} />
                <Route path="/finance/asset-depreciation" element={<AssetDepreciation />} />
                <Route path="/hr/exit-interviews" element={<ExitInterview />} />
                <Route path="/procurement/contract-renewals" element={<ContractRenewalTracker />} />
                <Route path="/inventory/demand-planning" element={<DemandPlanning />} />
                <Route path="/admin/compliance-audit" element={<ComplianceAuditLog />} />
                <Route path="/hr/learning-development" element={<LearningDevelopment />} />
                <Route path="/finance/profit-centers" element={<ProfitCenters />} />
                <Route path="/manufacturing/equipment-lifecycle" element={<EquipmentLifecycle />} />
                <Route path="/logistics/shipment-consolidation" element={<ShipmentConsolidation />} />
                <Route path="/crm/marketing-campaigns" element={<MarketingCampaigns />} />
                <Route path="/hr/workforce-analytics" element={<WorkforceAnalytics />} />
                <Route path="/finance/intercompany" element={<IntercompanyTransactions />} />
                <Route path="/procurement/vendor-scorecard" element={<VendorScorecard />} />
                <Route path="/inventory/audit-trail" element={<InventoryAuditTrail />} />
                <Route path="/manufacturing/yield-analysis" element={<ProductionYieldAnalysis />} />
                <Route path="/finance/tax-compliance" element={<TaxComplianceDashboard />} />
                <Route path="/hr/ess-requests" element={<ESSRequests />} />
                <Route path="/procurement/supplier-risk" element={<SupplierRiskAssessment />} />
                <Route path="/inventory/capacity-planner" element={<WarehouseCapacityPlanner />} />
                <Route path="/operations/contract-lifecycle" element={<ContractLifecycle />} />
                <Route path="/hr/shift-roster" element={<ShiftRoster />} />
                <Route path="/manufacturing/quality-control" element={<QualityControl />} />
                <Route path="/logistics/delivery-tracking" element={<DeliveryTracking />} />
                <Route path="/reports/custom" element={<CustomReports />} />

                {/* Retail Routes */}
                <Route path="/retail/pos" element={<POSTerminal />} />
                <Route path="/retail/loyalty" element={<LoyaltyPromotions />} />

                {/* Enterprise Infra Routes */}
                <Route path="/admin/workflow-builder-v2" element={<WorkflowBuilderPage />} />
                <Route path="/admin/custom-fields" element={<CustomFieldsManager />} />
                <Route path="/admin/session-management" element={<SessionManagementPage />} />
                <Route path="/admin/api-management" element={<APIManagementPage />} />

                <Route path="/sales" element={<SalesDashboard />} />
                <Route path="/sales/orders" element={<OrderProcessing />} />
                <Route path="/sales/fulfillment" element={<OrderFulfillment />} />
                <Route path="/sales/tracking" element={<OrderTracking />} />
                <Route path="/sales/returns" element={<ReturnsManagement />} />
                <Route path="/sales/targets" element={<SalesTargets />} />
                <Route path="/sales/pricing-rules" element={<PricingRules />} />
                <Route path="/sales/quote-conversion" element={<QuoteConversion />} />
                <Route path="/sales/delivery-notes" element={<DeliveryNotes />} />
                <Route path="/sales/price-lists" element={<PriceLists />} />
                <Route path="/sales/discounts" element={<DiscountsManagement />} />
                <Route path="/sales/tax-config" element={<TaxConfiguration />} />
                <Route path="/sales/shipping" element={<ShippingMethods />} />
                <Route path="/sales/payment-terms" element={<PaymentTerms />} />
                <Route path="/sales/templates" element={<OrderTemplates />} />
                <Route path="/sales/bulk-orders" element={<BulkOrders />} />
                <Route path="/sales/analytics" element={<SalesAnalytics />} />
                
                {/* Mobile Routes */}
                <Route path="/mobile" element={<MobileDashboard />} />
                <Route path="/mobile/offline-sync" element={<OfflineDataSync />} />
                <Route path="/mobile/field-ops" element={<FieldOperations />} />
                <Route path="/mobile/attendance" element={<MobileAttendance />} />
                <Route path="/mobile/expenses" element={<MobileExpenses />} />
                <Route path="/mobile/approvals" element={<MobileApprovals />} />
                <Route path="/mobile/location" element={<LocationTracking />} />
                <Route path="/mobile/reports" element={<MobileReports />} />
                <Route path="/mobile/notifications" element={<PushNotificationSettings />} />
                <Route path="/mobile/sales" element={<MobileSalesEntry />} />
                <Route path="/mobile/inventory-check" element={<MobileInventoryCheck />} />
                <Route path="/mobile/time-entry" element={<MobileTimeEntry />} />
              </Route>
              {/* Client Portal Routes */}
              <Route path="/portal/login" element={<Navigate to="/auth" replace />} />
              <Route path="/portal/accept-invite" element={<AcceptInvite />} />
              <Route path="/portal/reset-password" element={<PortalResetPassword />} />
              <Route path="/portal" element={<PortalLayout />}>
                <Route index element={<PortalDashboard />} />
                <Route path="invoices" element={<PortalInvoices />} />
                <Route path="payments" element={<PortalPayments />} />
                <Route path="projects" element={<PortalProjects />} />
                <Route path="deliverables" element={<PortalDeliverables />} />
                <Route path="messages" element={<PortalMessages />} />
                <Route path="settings" element={<PortalSettings />} />
                <Route path="documents" element={<PortalDocumentCenter />} />
                <Route path="support" element={<PortalSupportTickets />} />
              </Route>

              {/* Super Admin Routes */}
              <Route path="/superadmin/login" element={<Navigate to="/auth" replace />} />
              <Route path="/superadmin" element={<SuperAdminDashboard />} />
              <Route path="/superadmin/website" element={<ManageWebsite />} />
              <Route path="/superadmin/businesses/new" element={<CreateBusiness />} />
              <Route path="/superadmin/businesses" element={<AllBusinesses />} />
              <Route path="/superadmin/businesses/:id" element={<BusinessDetail />} />
              <Route path="/superadmin/pricing" element={<PricingPlans />} />
              <Route path="/superadmin/offers" element={<OffersDiscounts />} />
              <Route path="/superadmin/analytics" element={<SuperAdminAnalytics />} />
              <Route path="/superadmin/settings" element={<SuperAdminSettings />} />
              <Route path="/superadmin/audit" element={<PlatformAuditLogs />} />
              <Route path="/superadmin/trials" element={<TrialDashboard />} />
              <Route path="/superadmin/approvals" element={<SuperAdminApprovalQueue />} />
              <Route path="/superadmin/payments" element={<PaymentHistory />} />
              <Route path="/superadmin/inquiries" element={<ContactInquiries />} />
              <Route path="/superadmin/users" element={<SuperAdminUsers />} />
              <Route path="/superadmin/features" element={<FeatureControlCenter />} />

              {/* Short URL Redirect */}
              <Route path="/s/:token" element={<SetupRedirect />} />

              {/* Business Admin Routes */}
              <Route path="/business/login" element={<Navigate to="/auth" replace />} />
              <Route path="/business/setup" element={<BusinessSetup />} />
              <Route path="/business/team/invite" element={<InviteEmployee />} />

              {/* Employee Routes */}
              <Route path="/employee/login" element={<Navigate to="/auth" replace />} />
              <Route path="/employee/set-password" element={<EmployeeSetPassword />} />

              {/* Error Pages */}
              <Route path="/401" element={<Unauthorized />} />
              <Route path="/403" element={<Forbidden />} />

              {/* Dev Pages */}
              <Route path="/dev/isolation-test" element={<DataIsolationTest />} />

              <Route path="*" element={<NotFound />} />
                </Routes>
              </main>
              <AIAssistantPanel />
            </Suspense>
          </ErrorBoundary>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
